import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DeviceSyncField } from '@am3nd-org/pointcraft-types';

import { DeviceSyncResponseCompatibilitySchema } from 'src/utils/operator-authentication-access';

import { DEVICE_SYNC_STORAGE_KEY } from './storage';
import type { DeviceSyncData, DeviceSyncState, PersistedDeviceSyncState } from './types';

export * from './types';
export { DEVICE_SYNC_STORAGE_KEY } from './storage';

const initialData: DeviceSyncData = {
  businessData: null,
  userData: [],
  terminals: [],
  businessDevices: [],
  providerDetails: [],
  recurringPlans: [],
};

const initialPersistedState: PersistedDeviceSyncState = {
  ...initialData,
  tenantId: null,
  versions: {},
  cachedAt: null,
  availableFields: [],
};

const DEVICE_SYNC_PERSIST_VERSION = 3;

export function deduplicateTerminalsById(
  terminals: DeviceSyncData['terminals']
): DeviceSyncData['terminals'] {
  const seenTerminalIds = new Set<string>();

  return terminals.filter((terminal) => {
    if (seenTerminalIds.has(terminal.id)) return false;
    seenTerminalIds.add(terminal.id);
    return true;
  });
}

function parseAvailableFields(candidate: Partial<PersistedDeviceSyncState>): DeviceSyncField[] {
  const persistedFields = Array.isArray(candidate.availableFields)
    ? candidate.availableFields.filter((field): field is DeviceSyncField =>
        Object.values(DeviceSyncField).includes(field as DeviceSyncField)
      )
    : [];

  if (persistedFields.length || !candidate.cachedAt) return persistedFields;

  // Early V4 cache records persisted validated slices but did not reliably persist
  // their availability metadata. Those records were created by the full preload,
  // so treat the safe default slices as hydrated instead of refetching every boot.
  return [
    DeviceSyncField.BUSINESS_DATA,
    DeviceSyncField.USER_DATA,
    DeviceSyncField.TERMINALS,
    DeviceSyncField.BUSINESS_DEVICES,
    DeviceSyncField.PROVIDER_DETAILS,
    DeviceSyncField.RECURRING_PLANS,
  ];
}

export function parsePersistedDeviceSyncState(value: unknown): PersistedDeviceSyncState {
  if (!value || typeof value !== 'object') return initialPersistedState;

  const candidate = value as Partial<PersistedDeviceSyncState>;
  const parsed = DeviceSyncResponseCompatibilitySchema.safeParse({
    businessData: candidate.businessData ?? undefined,
    userData: candidate.userData,
    terminals: candidate.terminals,
    businessDevices: candidate.businessDevices,
    providerDetails: candidate.providerDetails,
    recurringPlans: candidate.recurringPlans,
  });

  if (!parsed.success) return initialPersistedState;

  return {
    businessData: parsed.data.businessData ?? null,
    userData: parsed.data.userData ?? [],
    terminals: deduplicateTerminalsById(parsed.data.terminals ?? []),
    businessDevices: parsed.data.businessDevices ?? [],
    providerDetails: parsed.data.providerDetails ?? [],
    recurringPlans: parsed.data.recurringPlans ?? [],
    tenantId: typeof candidate.tenantId === 'string' ? candidate.tenantId : null,
    versions:
      candidate.versions && typeof candidate.versions === 'object' ? candidate.versions : {},
    cachedAt: typeof candidate.cachedAt === 'string' ? candidate.cachedAt : null,
    availableFields: parseAvailableFields(candidate),
  };
}

export function migratePersistedDeviceSyncState(
  value: unknown,
  persistedVersion: number
): PersistedDeviceSyncState {
  if (persistedVersion < DEVICE_SYNC_PERSIST_VERSION) return initialPersistedState;
  return parsePersistedDeviceSyncState(value);
}

export function partializeDeviceSyncState(state: DeviceSyncState): PersistedDeviceSyncState {
  return {
    businessData: state.businessData,
    userData: state.userData,
    terminals: state.terminals,
    businessDevices: state.businessDevices,
    providerDetails: state.providerDetails,
    recurringPlans: state.recurringPlans,
    tenantId: state.tenantId,
    versions: state.versions,
    cachedAt: state.cachedAt,
    availableFields: state.availableFields,
  };
}

export const useDeviceSyncStore = create<DeviceSyncState>()(
  persist(
    (set, get) => ({
      ...initialPersistedState,
      status: 'idle',
      lastError: null,
      applySlices: (slices) => {
        const parsed = DeviceSyncResponseCompatibilitySchema.parse(slices);
        const businessId = parsed.businessData?.id ?? get().tenantId;

        const receivedFields = [
          ...(parsed.businessData !== undefined ? [DeviceSyncField.BUSINESS_DATA] : []),
          ...(parsed.userData !== undefined ? [DeviceSyncField.USER_DATA] : []),
          ...(parsed.terminals !== undefined ? [DeviceSyncField.TERMINALS] : []),
          ...(parsed.businessDevices !== undefined ? [DeviceSyncField.BUSINESS_DEVICES] : []),
          ...(parsed.providerDetails !== undefined ? [DeviceSyncField.PROVIDER_DETAILS] : []),
          ...(parsed.recurringPlans !== undefined ? [DeviceSyncField.RECURRING_PLANS] : []),
        ];

        set((state) => ({
          ...(parsed.businessData !== undefined ? { businessData: parsed.businessData } : {}),
          ...(parsed.userData !== undefined ? { userData: parsed.userData } : {}),
          ...(parsed.terminals !== undefined
            ? { terminals: deduplicateTerminalsById(parsed.terminals) }
            : {}),
          ...(parsed.businessDevices !== undefined
            ? { businessDevices: parsed.businessDevices }
            : {}),
          ...(parsed.providerDetails !== undefined
            ? { providerDetails: parsed.providerDetails }
            : {}),
          ...(parsed.recurringPlans !== undefined ? { recurringPlans: parsed.recurringPlans } : {}),
          tenantId: businessId,
          cachedAt: new Date().toISOString(),
          availableFields: Array.from(new Set([...state.availableFields, ...receivedFields])),
          status: 'ready',
          lastError: null,
          versions: state.versions,
        }));
      },
      setBusinessData: (businessData) => get().applySlices({ businessData }),
      updateBusinessData: (updater) => {
        const { businessData } = get();
        if (businessData) get().applySlices({ businessData: updater(businessData) });
      },
      updateFinancialDefaults: (financialDefaults) =>
        get().updateBusinessData((businessData) => ({
          ...businessData,
          financialDefaults,
        })),
      setOfflineModeEnabled: (offlineModeEnabled) =>
        get().updateBusinessData((businessData) => ({
          ...businessData,
          configurations: {
            ...businessData.configurations,
            offlineModeEnabled,
          },
        })),
      setVersions: (versions) => set((state) => ({ versions: { ...state.versions, ...versions } })),
      setStatus: (status, error = null) => set({ status, lastError: error }),
      clearTenantData: () =>
        set({
          ...initialPersistedState,
          status: 'idle',
          lastError: null,
        }),
      resetIfTenantChanged: (businessId) => {
        const currentTenantId = get().tenantId;
        if (!currentTenantId || currentTenantId === businessId) return false;
        get().clearTenantData();
        return true;
      },
    }),
    {
      name: DEVICE_SYNC_STORAGE_KEY,
      version: DEVICE_SYNC_PERSIST_VERSION,
      partialize: partializeDeviceSyncState,
      migrate: migratePersistedDeviceSyncState,
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...parsePersistedDeviceSyncState(persistedState),
      }),
      onRehydrateStorage: () => (state, error) => {
        if (!state) return;
        if (error) {
          state.clearTenantData();
          state.setStatus('error', 'Unable to hydrate the device-sync cache');
          return;
        }
        state.setStatus(state.cachedAt ? 'hydrated' : 'idle');
      },
    }
  )
);
